import { useEffect, useState } from 'react';
import { useParams, Link } from 'react-router-dom';
import { useAuth } from '@/context/AuthContext';
import { ContentCard } from '@/components/ContentCard';
import { User, Calendar, Trophy, Skull, Heart, Upload, Edit, Save } from 'lucide-react';
import { Textarea } from "@/components/ui/textarea";
import { toast } from 'sonner';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

export default function ProfilePage({ setTheaterContent }) {
  const { userId } = useParams();
  const { user: currentUser } = useAuth();
  const [profile, setProfile] = useState(null);
  const [uploads, setUploads] = useState([]);
  const [favorites, setFavorites] = useState([]);
  const [loading, setLoading] = useState(true);
  const [editingBio, setEditingBio] = useState(false);
  const [bio, setBio] = useState('');
  const [savingBio, setSavingBio] = useState(false);

  const isOwnProfile = currentUser?.user_id === userId;

  useEffect(() => {
    const fetchProfile = async () => {
      setLoading(true);
      try {
        const response = await fetch(`${API}/user/${userId}`);
        if (response.ok) {
          const data = await response.json();
          setProfile(data.user);
          setUploads(data.uploads);
          setFavorites(data.favorites);
          setBio(data.user.bio || '');
        }
      } catch (error) {
        console.error('Failed to fetch profile:', error);
      } finally {
        setLoading(false);
      }
    };

    fetchProfile();
  }, [userId]);

  const handleContentClick = async (content) => {
    try {
      const response = await fetch(`${API}/content/${content.content_id}`);
      if (response.ok) {
        const data = await response.json();
        setTheaterContent(data);
      }
    } catch (error) {
      console.error('Failed to load content:', error);
    }
  };

  const handleSaveBio = async () => {
    setSavingBio(true);
    try {
      const formData = new FormData();
      formData.append('bio', bio);

      const response = await fetch(`${API}/user/profile`, {
        method: 'PUT',
        credentials: 'include',
        body: formData
      });

      if (response.ok) {
        const updated = await response.json();
        setProfile(updated);
        setEditingBio(false);
        toast.success('Bio updated!');
      } else {
        toast.error('Failed to update bio');
      }
    } catch (error) {
      toast.error('Failed to update bio');
    } finally {
      setSavingBio(false);
    }
  };

  const handleAvatarChange = async (e) => {
    const file = e.target.files[0];
    if (!file) return;

    const formData = new FormData();
    formData.append('avatar', file);

    try {
      const response = await fetch(`${API}/user/profile`, {
        method: 'PUT',
        credentials: 'include',
        body: formData
      });

      if (response.ok) {
        const updated = await response.json();
        setProfile(updated);
        toast.success('Avatar updated!');
      } else {
        toast.error('Failed to update avatar');
      }
    } catch (error) {
      toast.error('Failed to update avatar');
    }
  };

  const formatDate = (dateStr) => {
    const date = new Date(dateStr);
    return date.toLocaleDateString('en-US', { year: 'numeric', month: 'long', day: 'numeric' });
  };

  const calculateLevel = (exp) => {
    return Math.floor(exp / 100) + 1;
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-[400px]">
        <p className="text-[#666] mono">LOADING PROFILE...</p>
      </div>
    );
  }

  if (!profile) {
    return (
      <div className="flex items-center justify-center min-h-[400px]">
        <div className="text-center">
          <User size={48} className="mx-auto mb-4 text-[#333]" />
          <p className="text-[#666]">User not found</p>
        </div>
      </div>
    );
  }

  return (
    <div className="space-y-6" data-testid="profile-page">
      {/* Profile header */}
      <div className="ws-profile-header">
        <div className="flex flex-col md:flex-row gap-6">
          {/* Avatar */}
          <div className="relative">
            {profile.avatar_url ? (
              <img 
                src={profile.avatar_url.startsWith('/') 
                  ? `${BACKEND_URL}${profile.avatar_url}` 
                  : profile.avatar_url}
                alt=""
                className="w-32 h-32 border-2 border-[#333]"
              />
            ) : (
              <div className="w-32 h-32 bg-[#1a1a1a] border-2 border-[#333] flex items-center justify-center">
                <User size={48} className="text-[#444]" />
              </div>
            )}
            {isOwnProfile && (
              <label className="absolute bottom-0 right-0 p-1 bg-[#cc0000] cursor-pointer hover:bg-[#ff3333] transition-colors">
                <Edit size={14} className="text-white" />
                <input 
                  type="file" 
                  accept="image/*" 
                  onChange={handleAvatarChange}
                  className="hidden"
                  data-testid="avatar-input"
                />
              </label>
            )}
          </div>

          {/* Info */}
          <div className="flex-1">
            <h1 className="text-2xl font-black text-white mb-1" data-testid="profile-username">
              {profile.username || profile.name}
            </h1>
            <div className="flex items-center gap-4 text-xs text-[#666] mb-4">
              <span className="flex items-center gap-1">
                <Calendar size={12} />
                Joined {formatDate(profile.join_date)}
              </span>
              <span className="text-[#cc0000] font-bold">
                LEVEL {calculateLevel(profile.experience || 0)}
              </span>
            </div>

            {/* Bio */}
            {editingBio ? (
              <div className="space-y-2">
                <Textarea
                  value={bio}
                  onChange={(e) => setBio(e.target.value)}
                  className="bg-black border-[#333] text-white"
                  placeholder="Write something about yourself..."
                  data-testid="bio-textarea"
                />
                <div className="flex gap-2">
                  <button
                    onClick={handleSaveBio}
                    disabled={savingBio}
                    className="btn-primary text-xs flex items-center gap-1"
                    data-testid="bio-save"
                  >
                    <Save size={12} />
                    {savingBio ? 'SAVING...' : 'SAVE'}
                  </button>
                  <button
                    onClick={() => {
                      setEditingBio(false);
                      setBio(profile.bio || '');
                    }}
                    className="btn-secondary text-xs"
                  >
                    CANCEL
                  </button>
                </div>
              </div>
            ) : (
              <div className="text-sm text-[#999]">
                {profile.bio || (isOwnProfile ? 
                  <span className="text-[#666] italic">No bio yet. Click edit to add one.</span> : 
                  <span className="text-[#666] italic">This user hasn't written a bio.</span>
                )}
                {isOwnProfile && (
                  <button
                    onClick={() => setEditingBio(true)}
                    className="ml-2 text-[#ffcc00] hover:text-[#ffd700] text-xs"
                    data-testid="bio-edit"
                  >
                    [EDIT]
                  </button>
                )}
              </div>
            )}
          </div>

          {/* Stats */}
          <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
            <div className="ws-stat-box">
              <div className="ws-stat-value text-lg">{profile.experience || 0}</div>
              <div className="ws-stat-label">EXP</div>
            </div>
            <div className="ws-stat-box">
              <Trophy size={20} className="mx-auto mb-1 text-[#ffd700]" />
              <div className="ws-stat-value text-lg">{profile.save_count || 0}</div>
              <div className="ws-stat-label">Saves</div>
            </div>
            <div className="ws-stat-box">
              <Skull size={20} className="mx-auto mb-1 text-[#cc0000]" />
              <div className="ws-stat-value text-lg">{profile.blam_count || 0}</div>
              <div className="ws-stat-label">Blams</div>
            </div>
            <div className="ws-stat-box">
              <Upload size={20} className="mx-auto mb-1 text-[#44ff44]" />
              <div className="ws-stat-value text-lg">{uploads.length}</div>
              <div className="ws-stat-label">Uploads</div>
            </div>
          </div>
        </div>
      </div>

      {/* Uploads */}
      <section data-testid="profile-uploads">
        <h2 className="text-lg font-bold text-white mb-4 flex items-center gap-2">
          <Upload size={18} className="text-[#cc0000]" />
          UPLOADS
        </h2>
        {uploads.length > 0 ? (
          <div className="grid grid-cols-2 md:grid-cols-4 lg:grid-cols-5 gap-3">
            {uploads.map((content) => (
              <ContentCard 
                key={content.content_id}
                content={content}
                onClick={() => handleContentClick(content)}
              />
            ))}
          </div>
        ) : (
          <div className="p-8 text-center border border-dashed border-[#333]">
            <Upload size={32} className="mx-auto mb-2 text-[#333]" />
            <p className="text-[#666]">No uploads yet</p>
            {isOwnProfile && (
              <Link to="/upload" className="btn-primary text-xs mt-4 inline-block">
                UPLOAD SOMETHING
              </Link>
            )}
          </div>
        )}
      </section>

      {/* Favorites */}
      <section data-testid="profile-favorites">
        <h2 className="text-lg font-bold text-white mb-4 flex items-center gap-2">
          <Heart size={18} className="text-[#cc0000]" />
          FAVORITES
        </h2>
        {favorites.length > 0 ? (
          <div className="grid grid-cols-2 md:grid-cols-4 lg:grid-cols-5 gap-3">
            {favorites.map((content) => (
              <ContentCard 
                key={content.content_id}
                content={content}
                onClick={() => handleContentClick(content)}
              />
            ))}
          </div>
        ) : (
          <div className="p-8 text-center border border-dashed border-[#333]">
            <Heart size={32} className="mx-auto mb-2 text-[#333]" />
            <p className="text-[#666]">No favorites yet</p>
          </div>
        )}
      </section>
    </div>
  );
}
